import { Middleware } from 'redux';
import { isRSAA, RSAA, RSAAAction, RSAACall } from 'redux-api-middleware';
import { Action } from '../modules/Actions';
import { getCsrfToken, setCsrfToken } from '../modules/Auth';
import { State } from '../types/State';
import { Dispatch } from '../types/Store';

export type ApiConfig = {
  host: string;
  webHost?: string;
  authorization?: string; // Authorization Header Value
  cookieName: string; // name of encrypted cookie from BFF
};

export function isAuthEndpoint(callApi: RSAACall): boolean {
  const apiUrl =
    callApi.endpoint && (callApi.endpoint as string).includes('?')
      ? (callApi.endpoint as string).split('?')[0]
      : (callApi.endpoint as string);

  return (
    typeof callApi.endpoint === 'string' &&
    (apiUrl.endsWith('login') || apiUrl.endsWith('refreshtoken'))
  );
}

function addCsrfToken(action: RSAAAction, state: State) {
  const callApi = action[RSAA];
  const token = getCsrfToken(state);

  if (callApi.method === 'GET' || token === undefined || isAuthEndpoint(callApi)) {
    return action;
  }

  callApi.headers = {
    ...callApi.headers,
    ...(token != null && { 'X-CSRF-TOKEN': token }),
  };

  return action;
}

function extractCsrfToken(dispatch: Dispatch, action: RSAAAction) {
  const callApi = action[RSAA];

  if (!isAuthEndpoint(callApi)) {
    return action;
  }

  if (typeof callApi.types[1] === 'string') {
    callApi.types[1] = {
      type: callApi.types[1],
      payload: (_apiAction: Action, _state: State, res: Response) => {
        const token = res.headers.get('X-CSRF-TOKEN');

        if (token) {
          dispatch(setCsrfToken(token));
        }

        return res.json().then((body: any) => {
          return body;
        });
      },
    };
  } else if (typeof callApi.types[1] === 'object') {
    const originalPayload: (apiAction: Action, state: State, res: Response) => any =
      callApi.types[1].payload;

    callApi.types[1].payload = (apiAction: Action, state: State, res: Response) => {
      const token = res.headers.get('X-CSRF-TOKEN');

      if (token) {
        dispatch(setCsrfToken(token));
      }

      return originalPayload(apiAction, state, res);
    };
  }

  return action;
}

/**
 * This middleware layer injects/replaces the [BARMER_HOST] key in the [RSAA].endpoint
 */
const apiConfigurationMiddleware = (config: ApiConfig): Middleware<{}, State, Dispatch> => (
  store,
) => (next) => (action) => {
  if (!isRSAA(action)) {
    return next(action);
  }

  const callApi = action[RSAA];
  if (callApi) {
    let endpoint = '';
    endpoint =
      typeof callApi.endpoint === 'function'
        ? (callApi.endpoint(store.getState()) as string)
        : callApi.endpoint;

    // Prepend API base URL to endpoint if it does not already contain a valid base URL.
    if (!/^((http|https|ftp):\/\/)/i.test(endpoint)) {
      endpoint = `${config.host}${endpoint}`;
    }

    const re = new RegExp(`^${config.host}`);
    const callingBFF: boolean = re.exec(endpoint) !== null;

    callApi.endpoint = endpoint;

    if (callingBFF) {
      // Web App: will not send cookies over CORS without this
      callApi.credentials = 'include';
    }

    // Add Authorization header
    if (callingBFF && config.authorization) {
      callApi.headers = {
        ...callApi.headers,
        Authorization: `Basic ${config.authorization}`,
      };
    }

    // eslint-disable-next-line no-param-reassign
    action = addCsrfToken(action, store.getState());
    // @ts-ignore Fix for mobile (for now)
    action = extractCsrfToken(next, action); // eslint-disable-line no-param-reassign
  }
  // Pass the FSA to the next action.
  return next(action);
};

export default apiConfigurationMiddleware;
